import { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useDownloadExportExcel } from "@/api/space/hooks/useSpace.compra-inteligente";
import type { QuickPanelMessage } from "../quickPanel.types";
import { resolveQuickPanelCallbackUrl } from "../utils/quickPanelParsers";

// --------------------------------- TYPES ---------------------------------
type UseQuickPanelActionsReturn = {
	readonly downloadMessageIds: ReadonlySet<string>;
	readonly handleAction: (message: QuickPanelMessage) => void;
};

// ---------------------------------- HOOK ----------------------------------
export function useQuickPanelActions(): UseQuickPanelActionsReturn {
	const { baixarExcel } = useDownloadExportExcel();
	const navigate = useNavigate();
	const pendingDownloadIdsRef = useRef(new Set<string>());
	const [downloadMessageIds, setDownloadMessageIds] = useState<ReadonlySet<string>>(() => new Set());

	const handleAction = useCallback(
		(message: QuickPanelMessage) => {
			const { action } = message;
			if (!action) return;

			switch (action.type) {
				case "download": {
					if (pendingDownloadIdsRef.current.has(message.id)) break;

					pendingDownloadIdsRef.current.add(message.id);
					setDownloadMessageIds(new Set(pendingDownloadIdsRef.current));
					baixarExcel(action.payload, {
						onSettled: () => {
							pendingDownloadIdsRef.current.delete(message.id);
							setDownloadMessageIds(new Set(pendingDownloadIdsRef.current));
						},
					});
					break;
				}
				case "redirect": {
					if (globalThis.location === undefined) break;

					const destination = resolveQuickPanelCallbackUrl(action.payload.url, globalThis.location.origin);
					if (destination?.type === "internal") navigate(destination.url);
					if (destination?.type === "external")
						globalThis.open(destination.url, "_blank", "noopener,noreferrer");

					break;
				}
			}
		},
		[baixarExcel, navigate],
	);

	return { downloadMessageIds, handleAction };
}
